import { useMemo } from "react";
import { useUser } from "../../contexts/UserContext";
import { useScopeParams } from "@/hooks/useScopeParams";
import { DataSection } from "./sections/DataSection";
import { EnrollmentSection } from "./sections/EnrollmentSection";
import { SafetySection } from "./sections/SafetySection";
import demoData from "./demo_data.json";
import { trials } from "@/utils/trials";
import Loader from "@/components/UI/Loader";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import { is403ApiError, useFetch } from "@/hooks/FetchHook";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { toBackendTrialId } from "@/utils/trialIdMap";
import { cloneTrialHomeData } from "./utils";
import TrialNewsDrawer from "./components/TrialNewsDrawer";
import { PortfolioTrialHomeScreen } from "./PortfolioTrialHomeScreen";
import type { PortfolioHomeData } from "./types";

import DemoEnrollmentSection from "./sections/demo/EnrollmentSection";
import DemoSubjectsSection from "./sections/demo/SubjectsSection";
import DemoSafetySection from "./sections/demo/SafetySection";
import DemoDataSection from "./sections/demo/DataSection";

type DistributionValue = number | { total: number };
type DistributionItem = {
  name: string | null | undefined;
  values: DistributionValue;
};
type CountItem = {
  name: string | null | undefined;
  values: Record<string, number>;
};
type AggregateMetric = number | Record<string, number>;
type TrialSpecificDataValue = number | DistributionItem[] | undefined;
type TrialSpecificData = Record<string, TrialSpecificDataValue>;

export type HomeData = {
  crf?: {
    overdue: number;
    required_sdv: number;
  };
  crf_overdue?: number;
  crf_required_sdv?: number;
  site_sdv?: {
    average: number;
    std: number;
    min: number;
    max: number;
  };
  site_sdv_average?: number;
  site_sdv_std?: number;
  site_sdv_min?: number;
  site_sdv_max?: number;
  mvr_action_items?: {
    open: number;
    open_60_days: number;
    closed: number;
  };
  mvr_action_items_open?: number;
  mvr_action_items_open_60_days?: number;
  mvr_action_items_closed?: number;
  protocol_deviations?: Record<string, Record<string, number>>;
  sites: {
    total?: number;
    total_sites?: number;
    countries: number;
    regions: number;
    open_stats: {
      average: number;
      std: number;
      range_low: number;
      range_high: number;
    };
  };
  enrollment_stats?: Record<string, AggregateMetric>;
  enrollment_stats_complete?: number;
  enrollment_stats_active?: number;
  enrollment_stats_early_termination?: number;
  screening_stats?: Record<string, AggregateMetric>;
  screening_stats_success?: number;
  screening_stats_in_progress?: number;
  screening_stats_failure?: number;
  enrollment_data: {
    sex: DistributionItem[];
    ipAdmin?: DistributionItem[];
    indication?: DistributionItem[];
    race: DistributionItem[];
    age?: DistributionItem[];
    age_grouped?: DistributionItem[];
    cumulativeDose?: DistributionItem[];
  };
  trial_specific_data?: TrialSpecificData;
  current_age_stats: Record<string, number>;
  aes?: CountItem[];
  taes?: CountItem[];
  teaes?: CountItem[];
  saes?: CountItem[];
  outOfRange?: CountItem[];
  doseHeldReduced?: CountItem[];
  doseReduced?: CountItem[];
  ipAES?: { name: string; values: { total: number } }[];
  ipAES_grade3_teaes?: number;
  ipAEs_SAEs?: number;
  // Fallback AE breakdown for trials whose source carries severity but no
  // outcome/causality vocabulary (see SafetySection).
  ae_severity?: CountItem[];
  saes_total?: number;
  pdParticipant?: CountItem[];
  pdSites?: CountItem[];
  discrepancies?: CountItem[];
};

export const TrialHomeScreen = () => {
  const { userInfo } = useUser();
  const { id } = useScopeParams();
  // The curated demo homepage keys off the backend trial, so it renders
  // identically whether the route uses the display id (DARATU-ALZ) or the
  // backend id (DEMO_TRIAL) — matching the cloud demo site.
  const isDemoTrial =
    id === trials.demo.DARATUMUMAB || toBackendTrialId(id) === "DEMO_TRIAL";
  const hasTrialNews = useFeatureFlag(FEATURE_FLAGS.TRIAL_NEWS);
  const {
    data: fetchedHomeData,
    isLoading,
    error,
  } = useFetch({
    endpointSuffix: "home",
    enabled: !!id && !isDemoTrial,
    useQueryOptions: {
      queryKey: ["trial-home", id],
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 2,
    },
  });

  const trialHomeData = useMemo(() => {
    const sourceData = (isDemoTrial ? demoData : fetchedHomeData) as
      | HomeData
      | null
      | undefined;

    if (!sourceData) {
      return null;
    }

    return cloneTrialHomeData(sourceData);
  }, [fetchedHomeData, isDemoTrial]);

  const isPortfolioResponse =
    !isDemoTrial &&
    fetchedHomeData != null &&
    (fetchedHomeData as { trial_type?: string }).trial_type === "portfolio";

  return (
    <div className="w-full p-6">
      {(isDemoTrial || hasTrialNews) && (
        <div className="mb-7 flex items-center justify-between gap-4">
          {isDemoTrial && userInfo?.first_name ? (
            <h1 className="text-[22px] font-semibold text-[#2B4752]">
              Welcome to Vivo, {userInfo.first_name}
            </h1>
          ) : (
            <span />
          )}
          <TrialNewsDrawer />
        </div>
      )}
      {!isDemoTrial && isLoading && (
        <div className="flex justify-center items-center">
          <Loader />
        </div>
      )}
      {!isDemoTrial && error && (
        <div className="flex mt-[100px] justify-center items-center">
          {is403ApiError(error) ? <Error403 /> : <ErrorComponent />}
        </div>
      )}
      {isDemoTrial && trialHomeData && (
        <div className="flex flex-col gap-9">
          <DemoEnrollmentSection data={trialHomeData} />
          <DemoSubjectsSection data={trialHomeData} />
          <DemoSafetySection data={trialHomeData} />
          <DemoDataSection data={trialHomeData} />
        </div>
      )}
      {isPortfolioResponse && !isLoading && !error && (
        <PortfolioTrialHomeScreen
          data={fetchedHomeData as unknown as PortfolioHomeData}
        />
      )}
      {!isDemoTrial &&
        !isPortfolioResponse &&
        trialHomeData &&
        !isLoading &&
        !error && (
          <div className="flex flex-col gap-9">
            <DataSection data={trialHomeData} />
            <EnrollmentSection data={trialHomeData} />
            <SafetySection data={trialHomeData} />
          </div>
        )}
    </div>
  );
};
